import { Link } from "react-router";
import { Alerta, Badge, Button, Card, Dialog, Row, Spinner, Stack, TextArea, TextField } from "@/components";
import type { HitoObra, SubHito } from "@/lib/api";
import { textoSubHitos } from "@/lib/avance";
import { formatFecha } from "@/lib/format";
import { useAvanceObraTab, type Objetivo } from "./useAvanceObraTab";

interface AccionesFila {
  nombre: string;
  primero: boolean;
  ultimo: boolean;
  deshabilitado: boolean;
  onSubir: () => void;
  onBajar: () => void;
  onEditar: () => void;
  onBorrar: () => void;
}

function Acciones({ nombre, primero, ultimo, deshabilitado, onSubir, onBajar, onEditar, onBorrar }: AccionesFila) {
  return (
    <Row envolver espacio={1}>
      <Button variante="fantasma" tamanio="sm" aria-label={`Subir ${nombre}`} disabled={primero || deshabilitado} onClick={onSubir}>
        ↑
      </Button>
      <Button variante="fantasma" tamanio="sm" aria-label={`Bajar ${nombre}`} disabled={ultimo || deshabilitado} onClick={onBajar}>
        ↓
      </Button>
      <Button variante="secundario" tamanio="sm" aria-label={`Editar ${nombre}`} onClick={onEditar}>
        Editar
      </Button>
      <Button variante="fantasma" tamanio="sm" aria-label={`Borrar ${nombre}`} onClick={onBorrar}>
        Borrar
      </Button>
    </Row>
  );
}

function FilaSub({ sub, numero, acciones }: { sub: SubHito; numero: number; acciones: Omit<AccionesFila, "nombre"> }) {
  return (
    <li className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 flex-col gap-1">
        <span>
          <span className="text-texto-suave">{`Hito ${numero}: `}</span>
          <span className="font-medium">{sub.nombre}</span>
        </span>
        {sub.descripcion && <span className="text-chico text-texto-suave">{sub.descripcion}</span>}
        {sub.completado_el && (
          <span>
            <Badge tono="exito">{`Cumplido el ${formatFecha(sub.completado_el)}`}</Badge>
          </span>
        )}
      </div>
      <Acciones nombre={sub.nombre} {...acciones} />
    </li>
  );
}

export function AvanceObraTab() {
  const t = useAvanceObraTab();
  const d = t.dialogo;
  const objetivoNombre = (o: Objetivo) => (o.sub ?? o.hito).nombre;

  const tituloForm =
    d?.tipo !== "form" ? "" : d.editando ? (d.editando.sub ? "Editar sub-hito" : "Editar hito") : d.padre ? `Nuevo sub-hito de ${d.padre.nombre}` : "Nuevo hito";

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Avance de obra</h1>
        <Button onClick={t.abrirNuevoHito}>Nuevo hito</Button>
      </Row>
      <p className="text-chico text-texto-suave">
        {"Los hitos, en orden, son el recorrido de la obra que ven los fiduciantes, con el % de sub-hitos cumplidos. Sin fechas: sólo avances. Los sub-hitos se marcan como cumplidos (con fotos y explicación) desde "}
        <Link to="/avance" className="text-primario underline">
          Avance
        </Link>
        .
      </p>
      <Alerta texto={t.errorMover} />
      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {!t.cargando && !t.errorCarga && t.hitos.length === 0 && (
        <Card>
          <Stack espacio={3}>
            <p className="text-texto-suave">
              Todavía no hay hitos. Cargá el recorrido del desarrollo (14 pasos, de la estructuración del fideicomiso a la entrega y escrituración, con
              sus 52 tareas por tipo) y después ajustalo, o empezá con "Nuevo hito".
            </p>
            <Row>
              <Button onClick={() => void t.cargarSugeridos()} disabled={t.cargandoSugeridos}>
                {t.cargandoSugeridos ? "Cargando…" : "Cargar el recorrido del desarrollo"}
              </Button>
            </Row>
            <Alerta texto={t.errorSugeridos} />
          </Stack>
        </Card>
      )}
      {t.hitos.length > 0 && (
        <ol aria-label="Hitos de la obra" className="flex flex-col gap-4">
          {t.hitos.map((h: HitoObra, i) => (
            <li key={h.id}>
              <Card>
                <section aria-labelledby={`hito-${h.id}`}>
                  <Stack espacio={4}>
                    <Row justificar="entre" alinear="inicio" envolver espacio={3}>
                      <Stack espacio={1}>
                        <h2 id={`hito-${h.id}`} className="text-titulo font-light tracking-tight">{`${i + 1}. ${h.nombre}`}</h2>
                        {h.descripcion && <p className="text-chico text-texto-suave">{h.descripcion}</p>}
                        <span className="text-chico text-texto-suave">{textoSubHitos(h)}</span>
                      </Stack>
                      <Acciones
                        nombre={h.nombre}
                        primero={i === 0}
                        ultimo={i === t.hitos.length - 1}
                        deshabilitado={t.moviendo}
                        onSubir={() => t.subir(h.id)}
                        onBajar={() => t.bajar(h.id)}
                        onEditar={() => t.abrirEditar({ hito: h, sub: null })}
                        onBorrar={() => t.abrirBorrar({ hito: h, sub: null })}
                      />
                    </Row>
                    {h.sub.length > 0 && (
                      <ol aria-label={`Sub-hitos de ${h.nombre}`} className="flex flex-col divide-y divide-borde">
                        {h.sub.map((s, j) => (
                          <FilaSub
                            key={s.id}
                            sub={s}
                            numero={j + 1}
                            acciones={{
                              primero: j === 0,
                              ultimo: j === h.sub.length - 1,
                              deshabilitado: t.moviendo,
                              onSubir: () => t.subir(s.id),
                              onBajar: () => t.bajar(s.id),
                              onEditar: () => t.abrirEditar({ hito: h, sub: s }),
                              onBorrar: () => t.abrirBorrar({ hito: h, sub: s }),
                            }}
                          />
                        ))}
                      </ol>
                    )}
                    <div>
                      <Button variante="secundario" tamanio="sm" aria-label={`Agregar sub-hito a ${h.nombre}`} onClick={() => t.abrirNuevoSub(h)}>
                        Agregar sub-hito
                      </Button>
                    </div>
                  </Stack>
                </section>
              </Card>
            </li>
          ))}
        </ol>
      )}

      <Dialog abierto={d?.tipo === "form"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={tituloForm}>
        <form onSubmit={(e) => void t.guardar(e)} noValidate>
          <Stack espacio={4}>
            <TextField etiqueta="Nombre" value={t.nombre} maxLength={120} onChange={(e) => t.setNombre(e.target.value)} error={t.errorNombre} />
            <TextArea
              etiqueta="Explicación"
              value={t.descripcion}
              filas={3}
              onChange={(e) => t.setDescripcion(e.target.value)}
              error={t.errorDescripcion}
              ayuda="Opcional. Qué es, en palabras simples: la ven los fiduciantes."
            />
            <Alerta texto={t.errorGuardar} />
            <Row justificar="fin" espacio={2}>
              <Button type="button" variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.guardando}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar">
        {d?.tipo === "borrar" && (
          <Stack espacio={4}>
            <p>
              {d.objetivo.sub
                ? `¿Borrar el sub-hito "${objetivoNombre(d.objetivo)}"?`
                : `¿Borrar el hito "${objetivoNombre(d.objetivo)}" con todos sus sub-hitos?`}
            </p>
            <Alerta texto={t.errorBorrar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
                Borrar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
